import React from 'react';

import HandyPageWrapper from '~Scenes/Handy/Components/HandyPageWrapper';
import { useDeviceInfo, useI18n, useLicenses, useSelector } from '~Hooks';
import SettingsToggle, { Setting } from '~Scenes/Handy/Components/SettingsToggle';
import { useStorage } from '~Components/Storage';
import { ORDER_STATUS } from '@baemingo/order-status-events/dist/types';
import RadioButtonSelect from '~ComponentsImproved/RadioSelector';
import styled from '@emotion/styled';
import { gql, useQuery } from '@apollo/client';
import { GetTicketTemplate, GetTicketTemplateVariables } from './__queries__';

const RadioButtonsBox = styled.div`
  border-radius: 8px;
  background: var(--colorBlueGray1100);
  margin-top: 16px;
`;

const TICKET_TEMPLATE_QUERY = gql`
  query GetTicketTemplate($venueId: String!) {
    operativeViewer {
      ticketTemplates(venueId: $venueId) {
        id
        stationIds
      }
    }
  }
`;

const KitchenTickets = () => {
  const { i18n } = useI18n();
  const licenses = useLicenses();
  const [storage, updateStorage] = useStorage();
  const venueId = useSelector((state) => state.venueId);

  // When enabling Epson retry, automatically enable BongShowPrintId to help identify duplicate prints
  const prevEpsonRetryRef = React.useRef(storage.enableEpsonRetry);
  React.useEffect(() => {
    if (storage.enableEpsonRetry && !prevEpsonRetryRef.current) {
      updateStorage({ BongShowPrintId: true });
    }
    prevEpsonRetryRef.current = storage.enableEpsonRetry;
  }, [storage.enableEpsonRetry]);

  const deviceInfo = useDeviceInfo();

  const ticketTemplateQuery = useQuery<GetTicketTemplate, GetTicketTemplateVariables>(
    TICKET_TEMPLATE_QUERY,
    {
      variables: { venueId: venueId! },
      skip: !venueId,
      fetchPolicy: 'cache-and-network',
    },
  );

  const templates = ticketTemplateQuery.data?.operativeViewer?.ticketTemplates || [];
  // Check if there is a template that is for all stations (aka default template).
  // If there is a template, we hide settings that are overridden by the templates to avoid confusion.
  const hasDefaultTemplate = templates.some((template) => template.stationIds.length === 0);

  const SETTING_LIST: Setting[] = [];

  /* These are currently only relevant for Zettle dock printer since those have support for bong prints */
  if (deviceInfo?.isZettleTerminal) {
    SETTING_LIST.push(
      {
        key: 'useHandheldPrinter',
        label: i18n('Handy.Settings.UseHandheldPrinter'),
      },
      {
        key: 'useHandheldBongPrinter',
        label: i18n('Handy.Settings.UseHandheldBongPrinter'),
      },
    );
  }

  if (licenses.hasHandheldPro || licenses.hasHandheldOrder) {
    SETTING_LIST.push({
      key: 'enableBongPrints',
      label: i18n('Settings.PrintKitchenBongSlips'),
    });
  }

  if ((licenses.hasHandheldPro || licenses.hasHandheldOrder) && storage.enableBongPrints) {
    // Only show settings that are not overridden by custom templates
    if (!hasDefaultTemplate) {
      SETTING_LIST.push({
        key: 'BongDisableCapitalLetters',
        label: i18n('Settings.Bong.DisableCapitalLetters'),
      });
      SETTING_LIST.push({
        key: 'BongHideAllExceptDesiredTime',
        label: i18n('Settings.Bong.HideAllExceptDesiredTime'),
        description: i18n('Settings.Bong.HideAllExceptDesiredTime.Text'),
      });
    }

    // These settings are not overridden by templates
    SETTING_LIST.push({
      key: 'bongPrintByStation',
      label: i18n('Settings.BongPrintByStation'),
      description: i18n('Settings.BongPrintByStation.Text'),
    });
    SETTING_LIST.push({
      key: 'bongGroupByStation',
      label: i18n('Settings.GroupKitchenBongsStation'),
      description: i18n('Settings.GroupKitchenBongsStation.Text'),
    });
    SETTING_LIST.push({
      key: 'bongGroupByCategory',
      label: i18n('Settings.GroupKitchenBongsCategory'),
      description: i18n('Settings.GroupKitchenBongsCategory.Text'),
    });

    // Only show settings that are not overridden by custom templates
    if (!hasDefaultTemplate) {
      SETTING_LIST.push({
        key: 'bongShowPrices',
        label: i18n('Settings.Bong.ShowPrices'),
        description: i18n('Settings.Bong.ShowPrices.Text'),
      });
      SETTING_LIST.push({
        key: 'bongShowFloorplan',
        label: i18n('Settings.Bong.ShowFloorplan'),
        description: i18n('Settings.Bong.ShowFloorplan.Text'),
      });
      SETTING_LIST.push({
        key: 'BongShowPrintId',
        label: i18n('Settings.Bong.ShowPrintId'),
        description: i18n('Settings.Bong.ShowPrintId.Text'),
      });
    }

    // These settings are not overridden by templates
    SETTING_LIST.push({
      key: 'enableEpsonRetry',
      label: i18n('Settings.Printer.EpsonRetry'),
      description: i18n('Settings.Printer.EpsonRetry.Text'),
    });
    SETTING_LIST.push({
      key: 'askToPrintBong',
      label: i18n('Settings.AskToPrintBong'),
      description: i18n('Settings.AskToPrintBong.Desc'),
    });
    SETTING_LIST.push({
      key: 'bongSortItemsAlphabetically',
      label: i18n('Settings.Bong.SortItemsAlphabetically'),
      description: i18n('Settings.Bong.SortItemsAlphabetically.Text'),
    });
  }

  SETTING_LIST.push({
    key: 'truncateReceiptItemNames',
    label: i18n('Settings.ShortenChequeItemNames'),
    description: i18n('Settings.ShortenChequeItemNames.Text'),
  });

  SETTING_LIST.push({
    key: 'printVoidedItems',
    label: i18n('Handy.Settings.PrintVoidedItems'),
  });

  const radioButtonsConfig: {
    containerLabel: string;
    containerDesc?: string;
    name: string;
    buttons: Array<{
      id: string;
      label: string;
      value: ORDER_STATUS | null;
    }>;
  }[] = [
    {
      containerLabel: i18n('Settings.KDS.PhysicalPrint'),
      containerDesc: i18n('Settings.KDS.PhysicalPrint.Desc'),
      name: 'status',
      buttons: [
        {
          id: 'NONE',
          label: i18n('Settings.KDS.NoPrint'),
          value: null,
        },
        {
          id: ORDER_STATUS.ACCEPTED,
          label: i18n('Settings.KDS.Accepted'),
          value: ORDER_STATUS.ACCEPTED,
        },
        {
          id: ORDER_STATUS.FINALIZING,
          label: i18n('Settings.KDS.Finalizing'),
          value: ORDER_STATUS.FINALIZING,
        },
        {
          id: ORDER_STATUS.READY,
          label: i18n('Settings.KDS.Ready'),
          value: ORDER_STATUS.READY,
        },
      ],
    },
  ];

  const experimentalList: Setting[] = [
    {
      key: 'useNewPrinterFlow',
      label: i18n('Settings.UseNewPrinterFlow'),
      description: i18n('Settings.UseNewPrinterFlow.Desc'),
    },
  ];

  if (storage.useNewPrinterFlow) {
    experimentalList.push({
      key: 'useImagePrinting',
      label: i18n('Settings.UseImagePrinting'),
      description: i18n('Settings.UseImagePrinting.Desc'),
    });
  }

  return (
    <HandyPageWrapper pageTitle={i18n('Handy.Settings.KitchenTickets')}>
      <SettingsToggle settingList={SETTING_LIST} />
      {(licenses.hasHandheldPro || licenses.hasHandheldOrder) && storage.enableBongPrints && (
        <RadioButtonsBox>
          {radioButtonsConfig.map((config) => (
            <RadioButtonSelect
              key={config.name}
              settingKey="KDSBongOnOrderStatus"
              data={config}
              selected={storage.KDSBongOnOrderStatus ?? 'NONE'}
              handleChange={(value) => updateStorage({ KDSBongOnOrderStatus: value })}
            />
          ))}
        </RadioButtonsBox>
      )}

      <SettingsToggle settingList={experimentalList} />
    </HandyPageWrapper>
  );
};

export default KitchenTickets;
